import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

export default function Login() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [registerCode, setRegisterCode] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: 'https://elitestraton.com/reset-password',
      });

      if (error) throw error;

      toast.success('E-mail de recuperação enviado! Verifique sua caixa de entrada.');
      setShowResetForm(false);
      setResetEmail('');
    } catch (error: any) {
      console.error('Reset error:', error);
      toast.error('Erro ao enviar e-mail de recuperação.');
    } finally {
      setLoading(false);
    }
  };
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await signIn(email, password);
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Auth error:', error);
      
      if (error.message === 'Invalid login credentials' || error.code === 'invalid_credentials') {
        toast.error('E-mail ou senha incorretos. Verifique suas credenciais.');
      } else if (error.code === 'user_not_found') {
        toast.error('Usuário não encontrado. Verifique o e-mail.');
      } else {
        toast.error('Erro na autenticação. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (registerCode !== '170500') {
      toast.error('Código de registro inválido');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) throw error;

      toast.success('Conta criada com sucesso! Você já pode fazer login.');
      setIsRegister(false);
      setRegisterCode('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Register error:', error);
      
      if (error.message?.includes('already registered')) {
        toast.error('Este e-mail já está cadastrado. Faça login normalmente.');
      } else {
        toast.error('Erro ao criar conta. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (isRegister) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center">
            <div className="relative">
              <div className="absolute -inset-1">
                <div className="w-full h-full mx-auto opacity-30 blur-lg filter bg-gradient-to-r from-[#bf953f] via-[#fcf6ba] to-[#aa771c]"></div>
              </div>
              <div className="relative bg-card p-4 rounded-2xl shadow-2xl border border-[#bf953f]/20">
                <img 
                  src="https://i.postimg.cc/mDsDsKS4/bbbb.png" 
                  alt="STRATON Logo" 
                  className="h-20 w-20 object-contain"
                />
              </div>
            </div>
            <h2 className="mt-8 text-4xl font-bold gold-text tracking-tight">STRATON</h2>
            <p className="mt-2 text-lg text-muted-foreground">Criar Nova Conta</p>
          </div>

          <div className="mt-12 bg-card rounded-2xl shadow-2xl p-8 border border-[#bf953f]/20">
            <form className="space-y-6" onSubmit={handleRegister}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="registerCode" className="block text-sm font-medium text-muted-foreground mb-2">
                    Código de Registro <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="registerCode"
                    name="registerCode"
                    type="text"
                    required
                    className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-input bg-accent text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                    placeholder="Digite o código de registro"
                    value={registerCode}
                    onChange={(e) => setRegisterCode(e.target.value.toUpperCase())}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-muted-foreground mb-2">
                    E-mail <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-input bg-accent text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                    placeholder="Digite seu e-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-muted-foreground mb-2">
                    Senha <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-input bg-accent text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                    placeholder="Crie uma senha (mín. 6 caracteres)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-muted-foreground mb-2">
                    Confirmar Senha <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-input bg-accent text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                    placeholder="Confirme sua senha"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <button
                  type="submit"
                  disabled={loading || !email || !password || !registerCode || !confirmPassword}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-primary-foreground bg-gradient-to-r from-[#bf953f] via-[#b38728] to-[#aa771c] hover:from-[#aa771c] hover:via-[#b38728] hover:to-[#bf953f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-foreground"></div>
                  ) : (
                    'Criar Conta'
                  )}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Já tem uma conta?{' '}
                <button
                  onClick={() => setIsRegister(false)}
                  className="text-[#bf953f] hover:text-[#aa771c] font-medium transition-colors"
                >
                  Faça login
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="absolute -inset-1">
              <div className="w-full h-full mx-auto opacity-30 blur-lg filter bg-gradient-to-r from-[#bf953f] via-[#fcf6ba] to-[#aa771c]"></div>
            </div>
            <div className="relative bg-card p-4 rounded-2xl shadow-2xl border border-[#bf953f]/20">
              <img 
                src="https://i.ibb.co/KkttKsR/logo-straton.png" 
                alt="STRATON Logo" 
                className="h-24 w-24 object-contain"
              />
            </div>
          </div>
          <h2 className="mt-8 text-4xl font-bold gold-text tracking-tight">STRATON</h2>
          <p className="mt-2 text-lg text-muted-foreground">Área do Trader</p>
        </div>

        <div className="mt-12 bg-card rounded-2xl shadow-2xl p-8 border border-[#bf953f]/20">
          <form className="space-y-6" onSubmit={handleLogin}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-muted-foreground mb-2">
                  E-mail
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-input bg-accent text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  placeholder="Digite seu e-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-muted-foreground mb-2">
                  Senha
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-input bg-accent text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-4">
              <button
                type="submit"
                disabled={loading || !email || !password}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-primary-foreground bg-gradient-to-r from-[#bf953f] via-[#b38728] to-[#aa771c] hover:from-[#aa771c] hover:via-[#b38728] hover:to-[#bf953f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-foreground"></div>
                ) : (
                  'Entrar'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            {!showResetForm ? (
              <>
                <button
                  onClick={() => setShowResetForm(true)}
                  className="text-primary hover:underline text-sm mr-4"
                >
                  Esqueci minha senha
                </button>
                <p className="text-sm text-muted-foreground">
                  Não tem uma conta ainda?{' '}
                  <button
                    onClick={() => setIsRegister(true)}
                    className="text-[#bf953f] hover:text-[#aa771c] font-medium transition-colors"
                  >
                    Registre-se
                  </button>
                </p>
              </>
            ) : (
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div>
                  <label htmlFor="resetEmail" className="block text-sm font-medium mb-2">
                    E-mail para recuperação
                  </label>
                  <input
                    id="resetEmail"
                    type="email"
                    required
                    className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="Digite o e-mail cadastrado"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-2 px-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  >
                    {loading ? 'Enviando...' : 'Enviar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowResetForm(false)}
                    className="flex-1 py-2 px-4 border border-input rounded-lg hover:bg-secondary transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}